import React, { useCallback, useMemo } from 'react';
import cx from 'classnames';
import { withParentSize } from '@visx/responsive';
import { scaleTime, scalePower } from '@visx/scale';
import { LinePath } from '@visx/shape';
import { curveMonotoneX } from '@visx/curve';
import { Grid } from '@visx/grid';
import { AxisRight } from '@visx/axis';
import { DateAxisBottom } from '../Elements/DateAxisBottom';
import { YearUnderlay } from '../Elements/YearUnderlay';
import { TooltipWithBounds, useTooltip } from '@visx/tooltip';
import { localPoint } from '@visx/event';
import { Icon } from 'js/components';
import { faStopwatch, faCalendarAlt } from '@fortawesome/pro-regular-svg-icons';

import styles from './CycleTime.module.scss';

const MIN_DATE_RANGE = 30 * 24 * 60 * 60 * 1000;
const SECONDS_PER_DAY = 86400;

/** Cube root exponent: spreads sub-day and multi-day values more evenly than log. */
const POWER_SCALE_EXPONENT = 1 / 3;

/** Tick sets by max range so labels stay readable. Lowest tick is always 1 day. */
const TICK_SETS = {
  small: [1, 2, 5, 10, 20], // maxDomain ≤ 25
  medium: [1, 5, 10, 25, 50, 100], // maxDomain ≤ 120
  large: [1, 10, 25, 50, 100, 200, 500], // maxDomain > 120
};

function getTickCandidatesForMax(maxDomain) {
  if (maxDomain <= 25) return TICK_SETS.small;
  if (maxDomain <= 120) return TICK_SETS.medium;
  return TICK_SETS.large;
}

function computePowerDomainAndTicks(minDaysInData, maxDaysInData) {
  const minDomain = Math.max(0, minDaysInData * 0.9);
  const maxDomain = maxDaysInData * 1.15;
  const domain = [minDomain, maxDomain];
  const candidates = getTickCandidatesForMax(maxDomain);
  let tickValues = candidates.filter((t) => t >= 1 && t <= maxDomain);
  if (tickValues.length === 0) {
    tickValues = [1];
  }
  return { domain, tickValues };
}

const tickLabelPropsFactory = (scale) => (d) => {
  const result = {
    fontSize: 9,
    strokeWidth: 0,
    paintOrder: 'stroke',
    textAnchor: 'start',
    y: scale(d) - 5,
    x: 4,
    className: 'tick-label',
  };

  return result;
};

const formatDate = (date) => {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
};

function CycleTimeChart({
  parentWidth: width,
  height = 300,
  margins = { top: 20, right: 20, bottom: 20, left: 20 },
  processedData,
  percentiles,
  scaleColorPercentile,
}) {
  margins = { top: 20, right: 20, bottom: 20, left: 20, ...margins };
  const chartWidth = width - margins.left - margins.right;
  const chartHeight = height - margins.top - margins.bottom;
  const { tasks, taskTypePaletteLookup } = processedData;

  const xScale = useMemo(() => {
    if (tasks.length === 0)
      return scaleTime({
        domain: [
          new Date(new Date().getTime() - MIN_DATE_RANGE),
          new Date().getTime(),
        ],
        range: [0, width - margins.left - margins.right],
      });

    const firstDate = tasks[0].date;
    const lastDate = tasks[tasks.length - 1].date;
    let startTime = firstDate.getTime();
    let endTime = lastDate.getTime();

    if (lastDate - firstDate < MIN_DATE_RANGE) {
      endTime = Math.min(
        new Date().getTime(),
        firstDate.getTime() + MIN_DATE_RANGE
      );
      startTime = endTime - MIN_DATE_RANGE;
    }

    return scaleTime({
      domain: [new Date(startTime), new Date(endTime)],
      range: [0, width - margins.left - margins.right],
    });
  }, [tasks, width, margins]);

  const { yScale, yTickValues } = useMemo(() => {
    const range = [chartHeight, 0];
    if (processedData.tasks.length === 0) {
      const scale = scalePower({
        domain: [0, 100],
        range,
        exponent: POWER_SCALE_EXPONENT,
      });
      return { yScale: scale, yTickValues: [1, 10, 100] };
    }
    const cycleTimesInDays = processedData.tasks.map(
      (task) => task.cycle_time_seconds / SECONDS_PER_DAY
    );
    const minDays = Math.min(...cycleTimesInDays);
    const maxDays = Math.max(...cycleTimesInDays);
    const { domain, tickValues } = computePowerDomainAndTicks(minDays, maxDays);
    const scale = scalePower({
      domain,
      range,
      exponent: POWER_SCALE_EXPONENT,
    });
    return { yScale: scale, yTickValues: tickValues };
  }, [processedData.tasks, chartHeight]);

  const formatCycleTime = useCallback(
    (days, excludeTop = true) => {
      if (excludeTop && yScale(days) < 15) {
        return '';
      }
      if (days < 60 / SECONDS_PER_DAY)
        return `${Math.round(days * SECONDS_PER_DAY)} seconds`;
      if (days < 3600 / SECONDS_PER_DAY)
        return `${Math.round((days * SECONDS_PER_DAY) / 60)} minutes`;
      if (days < 1)
        return `${Math.round((days * SECONDS_PER_DAY) / 3600)} hours`;
      const d = Math.round(10 * days) / 10;
      return d === 1 ? '1 day' : `${d} days`;
    },
    [yScale]
  );

  const x = useCallback((d) => xScale(d.date), [xScale]);
  const y = useCallback(
    (d) => yScale(d.cycle_time_seconds / SECONDS_PER_DAY),
    [yScale]
  );

  const {
    tooltipData,
    tooltipLeft,
    tooltipTop,
    tooltipOpen,
    showTooltip,
    hideTooltip,
  } = useTooltip();

  const handleTaskHover = useCallback(
    (event, task) => {
      const coords = localPoint(event.target.ownerSVGElement, event);
      showTooltip({
        tooltipLeft: coords.x,
        tooltipTop: coords.y,
        tooltipData: task,
      });
    },
    [showTooltip]
  );

  const handleTaskUnhover = useCallback(() => {
    hideTooltip();
  }, [hideTooltip]);

  return (
    <>
      <svg viewBox={`0 0 ${width} ${height}`}>
        <g transform={`translate(${margins.left}, ${margins.top})`}>
          <YearUnderlay height={chartHeight} xScale={xScale} fillMode="dark" />
          {/* Grid */}
          <Grid
            className="chart-grid chart-neutral-line"
            height={height - margins.top - margins.bottom}
            width={width - margins.left - margins.right}
            xScale={xScale}
            yScale={yScale}
            numTicksColumns={0}
            rowTickValues={yTickValues}
          />

          {/* Moving Average Line */}
          {/* {processedData.movingAverage.length > 0 && (
            <LinePath
              className={styles['moving-average-line']}
              data={processedData.movingAverage}
              x={(d) => xScale(d.date)}
              y={(d) => yScale(d.meanCycleTime / SECONDS_PER_DAY)}
              curve={curveMonotoneX}
              stroke="#ff6b35"
              strokeWidth={2}
              fill="none"
            />
          )} */}
          {percentiles.map((x) => (
            <LinePath
              key={x}
              className={styles['moving-average-linez']}
              data={processedData.movingAverage}
              x={(d) => xScale(d.date)}
              y={(d) => yScale(d[x] / SECONDS_PER_DAY)}
              curve={curveMonotoneX}
              stroke={scaleColorPercentile(x)}
              strokeWidth={2}
              fill="none"
            />
          ))}

          {/* Axes */}
          <DateAxisBottom
            chartHeight={chartHeight}
            chartWidth={chartWidth}
            xScale={xScale}
          />
          <AxisRight
            scale={yScale}
            axisClassName="chart-axis"
            tickFormat={formatCycleTime}
            tickLineProps={{ className: 'tick' }}
            tickLabelProps={tickLabelPropsFactory(yScale)}
            tickValues={yTickValues}
          />

          {/* Individual Task Dots */}
          <g className={styles['task-dots']}>
            {processedData.tasks.map((task, index) => (
              <g
                key={`${task.key}`}
                className={`${taskTypePaletteLookup.get(task.task_type)}`}
              >
                <circle
                  cx={x(task)}
                  cy={y(task)}
                  r={tooltipData?.key === task.key ? 6 : 4}
                  className={cx(styles['task-dot'], 'palette-fill', {
                    [styles['task-dot-hovered']]: tooltipData?.key === task.key,
                  })}
                  onMouseEnter={(event) => handleTaskHover(event, task)}
                  onMouseLeave={handleTaskUnhover}
                />
              </g>
            ))}
          </g>
        </g>
      </svg>
      {/* Tooltip */}
      {tooltipOpen && tooltipData && (
        <TooltipWithBounds
          left={tooltipLeft}
          top={tooltipTop}
          className={styles['tooltip']}
        >
          <div className={styles['tooltip-title']}>
            <span className={styles['tooltip-key']}>
              {tooltipData.key != null ? tooltipData.key + ' ' : ''}
            </span>
            {tooltipData.title}
          </div>
          <div className={styles['tooltip-details']}>
            <div>
              <Icon icon={faStopwatch} className="icon icon-left" />
              {formatCycleTime(
                tooltipData.cycle_time_seconds / SECONDS_PER_DAY,
                false
              )}
            </div>
            <div>
              <Icon icon={faCalendarAlt} className="icon icon-left" />
              {formatDate(tooltipData.date)}
            </div>
            {tooltipData.assignee && (
              <div>Assignee: {tooltipData.assignee}</div>
            )}
          </div>
        </TooltipWithBounds>
      )}
    </>
  );
}

export default withParentSize(CycleTimeChart);
